import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  importProductsApi,
  getImportJobApi,
  getImportJobsHistoryApi,
  getProductsApi,
  getProductApi,
  createProductApi,
  updateProductApi,
  deleteProductApi,
  type ImportProductsParams,
} from "../api/productsApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { ImportJobId, ProductId } from "@KassaroAB/shared/types/ids";
import { isActiveJobStatus } from "@KassaroAB/shared/utils/jobStatus";
import type {
  RestCreateProduct,
  RestUpdateProduct,
} from "@KassaroAB/shared/types/product";

const productsQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "products");

const invalidateProductQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({ queryKey: productsQueryKey(scope) });
};

export const useGetProductsQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: productsQueryKey(scope),
    queryFn: () => getProductsApi(scope!),
    enabled: !!scope,
  });
};

export const useGetProductQuery = (
  scope: CatalogScope | null,
  productId: ProductId | null,
) => {
  return useQuery({
    queryKey: [...productsQueryKey(scope), productId],
    queryFn: () => getProductApi(scope!, productId!),
    enabled: !!scope && !!productId,
  });
};

export const useCreateProductMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (product: RestCreateProduct) =>
      createProductApi(scope!, product),
    onSuccess: () => {
      invalidateProductQueries(queryClient, scope);
    },
  });
};

export const useUpdateProductMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      product,
    }: {
      productId: ProductId;
      product: RestUpdateProduct;
    }) => updateProductApi(scope!, productId, product),
    onSuccess: (_, variables) => {
      invalidateProductQueries(queryClient, scope);
      queryClient.invalidateQueries({
        queryKey: [...productsQueryKey(scope), variables.productId],
      });
    },
  });
};

export const useDeleteProductMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: ProductId) =>
      deleteProductApi(scope!, productId),
    onSuccess: () => {
      invalidateProductQueries(queryClient, scope);
    },
  });
};

export const useImportProductsMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ImportProductsParams) => {
      if (!scope) {
        throw new Error("Catalog scope is required");
      }
      return importProductsApi(scope, params);
    },
    onSuccess: () => {
      if (scope) {
        invalidateProductQueries(queryClient, scope);
        queryClient.invalidateQueries({
          queryKey: catalogQueryKey(scope, "importJobs"),
        });
      }
    },
  });
};

export const useGetImportJobQuery = (
  scope: CatalogScope | null,
  importJobId: ImportJobId | null,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [...catalogQueryKey(scope, "importJobs"), importJobId],
    queryFn: () => getImportJobApi(scope!, importJobId!),
    enabled: !!scope && !!importJobId && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && isActiveJobStatus(status)) {
        return 10000;
      }
      return false;
    },
  });
};

export const useGetImportJobsHistoryQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: catalogQueryKey(scope, "importJobs"),
    queryFn: () => getImportJobsHistoryApi(scope!),
    enabled: !!scope,
    refetchInterval: 10000,
  });
};
